import type { Session } from '@/types/auth';
import { isAxiosError, AxiosError, AxiosHeaders, isCancel } from 'axios';

import type { RetryableAxiosRequestConfig } from '@/types/api';

const SEMVER_REGEX: RegExp = /^\d+\.\d+\.\d+$/;

/**
 * Determines whether a given error indicates that the application is offline or has encountered a network-level
 * failure.
 *
 * This helper consolidates several heuristics:
 * - `navigator.onLine` is `false` (the browser reports no network connection).
 * - Axios network errors without a response (excluding canceled requests).
 * - Fetch-related TypeErrors that commonly signal lost connectivity.
 * - Dynamic import / chunk load failures (common when a network is disrupted).
 *
 * @param error - The error object thrown during rendering or data fetching.
 * @returns `true` if the error likely indicates an offline state or network-level failure, otherwise `false`.
 */
export function checkIfOffline(error: unknown): boolean {
  const errorString: string = toErrorMessage(error);

  // Detect offline / network-level failures:
  const isAxiosNetworkFailure: boolean = isAxiosError(error) && !error.response && !isCancel(error);

  // Some browsers throw TypeError("Failed to fetch") or “NetworkError when attempting to fetch resource.”
  const isFetchNetworkFailure: boolean = error instanceof TypeError &&
    /NetworkError|Failed to fetch/i.test(String(error.message));

  // Dynamic import / chunk load failures commonly show one of these phrases:
  const isChunkLoadFailure: boolean = /Failed to fetch dynamically imported module|Load failed|Loading chunk [\w-]+ failed|ChunkLoadError|Importing a module script failed/i.test(errorString);

  return (!navigator.onLine || isAxiosNetworkFailure || isFetchNetworkFailure || isChunkLoadFailure);
}

/**
 * Retrieve and validate the API version from Vite environment variables.
 *
 * Ensures that VITE_API_VERSION is defined and matches the expected semantic version format (x.y.z).
 *
 * @param env - The Vite ImportMetaEnv object.
 * @returns The validated API version string.
 * @throws Error if the version is missing or invalid.
 */
export function getApiVersion(env: ImportMetaEnv): string {
  const version: string | undefined = env.VITE_API_VERSION?.trim();
  if (!version) {
    throw new Error("Missing VITE_API_VERSION. Define it in your .env.local file (required at build time).")
  }
  if (!SEMVER_REGEX.test(version)) {
    throw new Error(`Invalid VITE_API_VERSION: "${version}". Expected format x.y.z`);
  }
  return version;
}

/**
 * Retrieve the value of a cookie by its name.
 *
 * @param name - The name of the cookie to retrieve.
 * @returns The value of the cookie if it exists; otherwise, an empty string.
 */
export function getCookie(name: string): string {
  return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
}

/**
 * Determine whether the provided headers contain an Authorization header. Supports both AxiosHeaders instances and
 * plain header objects.
 *
 * @param headers - The request headers.
 * @returns True if an Authorization header is present; otherwise, false.
 */
export function hasAuthHeader (headers: any) {
  if (headers instanceof AxiosHeaders) {
    return !!headers.get('Authorization');
  }
  return !!(headers?.Authorization ?? headers?.authorization);
}

// noinspection SpellCheckingInspection
/**
 * Check whether a Django session cookie is present.
 *
 * Note:
 * Django sessions are only ever created server-side. This front-end does not initiate cookie-based sessions directly.
 *
 * @returns True if a loginid cookie exists.
 */
export function hasCookieSession() {
  return !!getCookie('loginid');
}

/**
 * Determine whether a valid JWT session exists in sessionStorage.
 *
 * @returns True if a stored session with an access token is present.
 */
export function hasJwtSession() {
  return !!retrieveSessionFromStorage()?.accessToken;
}

/**
 * Verify that an object conforms to the `Session` interface shape.
 *
 * This function performs a runtime type guard check to ensure the object has the
 * required properties (`id`, `accessToken`, and `refreshToken`), all of which must be strings.
 *
 * @param obj - The object to test.
 * @returns `true` if the object matches the `Session` type; otherwise, `false`.
 */
function isSession(obj: any): obj is Session {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.id === 'string' &&
    typeof obj.accessToken === 'string' &&
    typeof obj.refreshToken === 'string'
  );
}

/**
 * Type guard to check whether an error is an AxiosError caused by lack of authorization.
 *
 * Specifically, this checks for HTTP status codes:
 * - 401 (Unauthorized)
 * - 403 (Forbidden)
 *
 * @param error - The error object to inspect.
 * @returns `true` if the error is an AxiosError with status 401 or 403; otherwise `false`.
 */
export function isUnauthorized(error: unknown): error is AxiosError {
  if (!isAxiosError(error)) return false;
  const status: number | undefined = error.response?.status;
  return status === 401 || status === 403;
}

/**
 * Retrieve the session object from session storage and validate its shape.
 *
 * @returns A valid Session object if found and valid; otherwise, null.
 */
export function retrieveSessionFromStorage(): Session | null {
  const sessionJson: string | null = sessionStorage.getItem("session");
  if (!sessionJson) return null;

  try {
    const parsed = JSON.parse(sessionJson);
    if (isSession(parsed)) return parsed;
    console.warn("Invalid session object found in storage.");
  } catch (error) {
    console.error("Failed to parse session from storage:", error);
  }

  return null
}

/**
 * Determine whether a failed request should be retried using a refreshed JWT access token.
 *
 * A retry is permitted only when:
 * - The error is a 401 Unauthorized response,
 * - The request has not already been retried,
 * - The original request included an Authorization header.
 *
 * @param error - The Axios error thrown by the failed request.
 * @param config - The original request configuration.
 * @returns True if a token refresh retry should be attempted; otherwise, false.
 */
export function shouldRetryWithRefresh(
  error: AxiosError,
  config: RetryableAxiosRequestConfig | undefined
): config is RetryableAxiosRequestConfig {
  if (!config) return false;
  if (error.response?.status !== 401) return false;
  if (config.sent === true) return false;
  return hasAuthHeader(config.headers);
}

/**
 * Determine whether requests should use Django session (cookie-based) authentication.
 *
 * Cookie authentication is only allowed when:
 * - Cookie authentication is enabled as an environment variable via VITE_ENABLE_COOKIE_AUTH=true, and
 * - No JWT session is active, and
 * - A Django session cookie is already present.
 *
 * Sessions are intentionally created only by the Django server. This front-end does not initiate cookie sessions.
 *
 * @param env - Vite environment variables (import.meta.env).
 * @returns True if cookie-based authentication should be used.
 */
export function shouldUseCookieAuth(env: ImportMetaEnv): boolean {
  const cookieAuthEnabled: boolean = env.VITE_ALLOW_COOKIE_AUTH === 'true';
  return cookieAuthEnabled && !hasJwtSession() && hasCookieSession();
}

/**
 * Convert an unknown error into a human-readable message.
 *
 * If the error is an AxiosError:
 *   - Prefer the `message` field from the response data (if available).
 *   - Otherwise, use `statusText`, then the error's own message.
 *
 * If the error is a generic Error, use its message. Otherwise, coerce the error to a string.
 *
 * @param error - The error to convert.
 * @returns A string message describing the error.
 */
export function toErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error.message : String(error);
  }

  const message: any = (error.response?.data as any)?.message;
  if (typeof message === "string") {
    return message;
  }

  return error.response?.statusText ?? error.message ?? String(error);
}